import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Card,
  BlockStack,
  Text,
  Checkbox,
  Button,
  FormLayout,
  Divider,
  Box,
  InlineStack,
  Tabs,
  EmptyState,
  Bleed,
} from "@medentic/polaris-react";
import {
  PlusCircleIcon,
  EditIcon,
  PlusIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { DatePickerField } from "@shared/ui";
import {
  Event,
  AgendaDay,
  AgendaSession,
  AgendaSwimlane,
} from "@features/events/types";
import AddEditSessionModal from "./AddEditSessionModal";
import ManageTracksModal from "./ManageTracksModal";
import AddEditDayModal from "./AddEditDayModal";
import { formatDate } from "@shared/lib";
import { useParams } from "react-router-dom";
import { getLocalizedText } from "@shared/lib";
import {
  sortSessionsByTime,
  normalizeAgendaData,
} from "./agendaUtils";
import { TimezoneAutoComplete } from "@shared/ui";
import { Timezone } from "@shared/types/commonTypes";
import { SessionsListRenderer } from "./SessionsList";

interface AgendaCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

// ========================
// CONFIGURATION RENDERER
// ========================
interface ConfigurationRendererProps {
  isMultipleDays: boolean;
  hasMultipleTracks: boolean;
  eventDate: string;
  onMultipleDaysChange: (checked: boolean) => void;
  onMultipleTracksChange: (checked: boolean) => void;
  onEventDateChange: (date: string) => void;
  agenda: AgendaDay[];
  onAddDay: () => void;
  onAddNextDay: () => void;
  onEditDay: (dayIndex: number) => void;
  onRemoveDay: (dayIndex: number) => void;
}

const ConfigurationRenderer: React.FC<ConfigurationRendererProps> = ({
  isMultipleDays,
  hasMultipleTracks,
  eventDate,
  onMultipleDaysChange,
  onMultipleTracksChange,
  onEventDateChange,
  agenda,
  onAddDay,
  onAddNextDay,
  onEditDay,
  onRemoveDay,
}) => {
  const { t } = useTranslation();

  return (
    <FormLayout>
      <Checkbox
        label={t("eventDetails.agenda.multipleDaysCheckbox")}
        checked={isMultipleDays}
        onChange={onMultipleDaysChange}
      />

      {!isMultipleDays && (
        <Box maxWidth="200px">
          <DatePickerField
            label={t("eventDetails.agenda.eventDate")}
            value={eventDate}
            onChange={onEventDateChange}
            disabled={false}
          />
        </Box>
      )}
      {/* Multiple Days Management */}
      {isMultipleDays && (
        <MultipleDaysRenderer
          agenda={agenda}
          onAddDay={onAddDay}
          onAddNextDay={onAddNextDay}
          onEditDay={onEditDay}
          onRemoveDay={onRemoveDay}
          formatDate={formatDate}
        />
      )}

      <Checkbox
        label={t("eventDetails.agenda.multipleTracksCheckbox")}
        checked={hasMultipleTracks}
        onChange={onMultipleTracksChange}
      />
    </FormLayout>
  );
};

// ========================
// MULTIPLE DAYS RENDERER
// ========================
interface MultipleDaysRendererProps {
  agenda: AgendaDay[];
  onAddDay: () => void;
  onAddNextDay: () => void;
  onEditDay: (index: number) => void;
  onRemoveDay: (index: number) => void;
  formatDate: (date: string) => string;
}

const MultipleDaysRenderer: React.FC<MultipleDaysRendererProps> = ({
  agenda,
  onAddDay,
  onAddNextDay,
  onEditDay,
  onRemoveDay,
  formatDate,
}) => {
  const { t } = useTranslation();

  // Sort agenda by date for consistent display
  const sortedAgenda = agenda
    .map((day, originalIndex) => ({ day, originalIndex }))
    .sort(
      (a, b) => new Date(a.day.date).getTime() - new Date(b.day.date).getTime()
    );

  const handleAddNextDay = () => {
    onAddNextDay();
  };

  return (
    <BlockStack gap="300">
      {agenda.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm">
            {t("eventDetails.agenda.daysTitle")}
          </Text>
          <Button
            icon={PlusCircleIcon}
            variant="plain"
            size="slim"
            onClick={onAddDay}
          >
            {t("eventDetails.agenda.addDay")}
          </Button>
        </InlineStack>
      )}

      {agenda.length === 0 ? (
        <EmptyState
          heading={t("eventDetails.agenda.noDaysAdded")}
          action={{
            content: t("eventDetails.agenda.addDay"),
            onAction: onAddDay,
          }}
          image=""
        >
          <Text as="p">{t("eventDetails.agenda.addFirstDay")}</Text>
        </EmptyState>
      ) : (
        <BlockStack gap="200">
          {sortedAgenda.map(({ day, originalIndex }) => {
            return (
              <Box
                key={`day-${originalIndex}-${day.date}`}
                padding="300"
                borderWidth="025"
                borderColor="border"
                borderRadius="200"
                background="bg-surface-secondary"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd" fontWeight="medium">
                    {formatDate(day.date)}
                  </Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Button
                      icon={EditIcon}
                      variant="plain"
                      size="slim"
                      onClick={() => onEditDay(originalIndex)}
                    />
                    <Button
                      icon={DeleteIcon}
                      variant="plain"
                      size="slim"
                      tone="critical"
                      onClick={() => onRemoveDay(originalIndex)}
                    />
                  </InlineStack>
                </InlineStack>
              </Box>
            );
          })}

          {/* Add Next Day Button */}
          <InlineStack align="start">
            <Button
              variant="plain"
              icon={PlusCircleIcon}
              onClick={handleAddNextDay}
            >
              {t("eventDetails.agenda.addNextDay")}{" "}
            </Button>
          </InlineStack>
        </BlockStack>
      )}
    </BlockStack>
  );
};

// ========================
// EMPTY STATE RENDERER
// ========================
interface EmptyStateRendererProps {
  type: "noAgenda" | "noSessions" | "noTracks";
  onAction?: () => void;
  actionLabel?: string;
}

const EmptyStateRenderer: React.FC<EmptyStateRendererProps> = ({
  type,
  onAction,
  actionLabel,
}) => {
  const { t } = useTranslation();

  if (type === "noAgenda") {
    return (
      <EmptyState
        heading={t("eventDetails.agenda.program.emptyState")}
        action={
          onAction && actionLabel
            ? {
                content: actionLabel,
                onAction,
              }
            : undefined
        }
        image=""
      >
        <Text as="p">
          {t("eventDetails.agenda.program.emptyStateDescription")}
        </Text>
      </EmptyState>
    );
  }

  if (type === "noTracks") {
    return (
      <Box padding="400" borderRadius="200">
        <BlockStack gap="300" align="center" inlineAlign="center">
          <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
            {t("eventDetails.agenda.tracks.emptyStateDescription")}
          </Text>
          {onAction && (
            <Box>
              <Button
                icon={PlusIcon}
                variant="primary"
                size="medium"
                onClick={onAction}
              >
                {actionLabel || t("eventDetails.agenda.tracks.addTrack")}
              </Button>
            </Box>
          )}
        </BlockStack>
      </Box>
    );
  }

  if (type === "noSessions") {
    return (
      <Box paddingBlock="300">
        <BlockStack gap="200">
          <Box padding="300">
            <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
              {t("eventDetails.agenda.noSessions")}
            </Text>
          </Box>
          {onAction && (
            <InlineStack align="center">
              <Button
                icon={PlusIcon}
                variant="primary"
                size="slim"
                onClick={onAction}
              >
                {actionLabel || t("eventDetails.agenda.program.addButton")}
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      </Box>
    );
  }

  return null;
};

// ========================
// PROGRAM CONTENT RENDERER
// ========================
interface ProgramContentRendererProps {
  agenda: AgendaDay[];
  isMultipleDays: boolean;
  hasMultipleTracks: boolean;
  selectedTrackIndex: number;
  formatDate: (date: string) => string;
  onAddSession: (dayIndex: number, swimlaneIndex: number) => void;
  onEditSession: (
    session: AgendaSession,
    sessionIndex: number,
    dayIndex: number,
    swimlaneIndex: number
  ) => void;
  onRemoveSession: (
    dayIndex: number,
    swimlaneIndex: number,
    sessionIndex: number
  ) => void;
  onDuplicateSession: (
    session: AgendaSession,
    dayIndex: number,
    swimlaneIndex: number
  ) => void;
  onManageTracks?: () => void;
}

const ProgramContentRenderer: React.FC<ProgramContentRendererProps> = ({
  agenda,
  isMultipleDays,
  hasMultipleTracks,
  selectedTrackIndex,
  formatDate,
  onAddSession,
  onEditSession,
  onRemoveSession,
  onDuplicateSession,
  onManageTracks,
}) => {
  // Sort agenda by date for consistent display
  const sortedAgenda = agenda
    .map((day, dayIndex) => ({ day, dayIndex }))
    .sort(
      (a, b) => new Date(a.day.date).getTime() - new Date(b.day.date).getTime()
    );

  return (
    <BlockStack gap="400">
      {sortedAgenda.map(({ day, dayIndex }) => {
        return (
          <div key={`program-day-${dayIndex}-${day.date}`}>
            {isMultipleDays && (
              <Box
                padding="200"
                background="bg-surface-secondary"
                borderRadius="100"
              >
                <Text as="h4" variant="bodyMd" fontWeight="medium">
                  {formatDate(day.date)}
                </Text>
              </Box>
            )}

            {hasMultipleTracks ? (
              // Multi-track view - check if tracks exist
              day.swimlines.length === 0 ? (
                <EmptyStateRenderer
                  type="noTracks"
                  onAction={onManageTracks}
                />
              ) : day.swimlines[selectedTrackIndex] ? (
                <BlockStack gap="200">
                  <SessionsListRenderer
                    sessions={day.swimlines[selectedTrackIndex]?.sessions || []}
                    dayIndex={dayIndex}
                    swimlaneIndex={selectedTrackIndex}
                    onEditSession={(session, sessionIndex) =>
                      onEditSession(
                        session,
                        sessionIndex,
                        dayIndex,
                        selectedTrackIndex
                      )
                    }
                    onRemoveSession={(sessionIndex) =>
                      onRemoveSession(
                        dayIndex,
                        selectedTrackIndex,
                        sessionIndex
                      )
                    }
                    onDuplicateSession={(session) =>
                      onDuplicateSession(session, dayIndex, selectedTrackIndex)
                    }
                    onAddSession={() =>
                      onAddSession(dayIndex, selectedTrackIndex)
                    }
                  />
                </BlockStack>
              ) : null
            ) : (
              // Single track view - show all swimlanes
              day.swimlines.map(
                (swimlane: AgendaSwimlane, swimlaneIndex: number) => (
                  <SessionsListRenderer
                    key={swimlaneIndex}
                    sessions={swimlane.sessions}
                    dayIndex={dayIndex}
                    swimlaneIndex={swimlaneIndex}
                    onEditSession={(session, sessionIndex) =>
                      onEditSession(
                        session,
                        sessionIndex,
                        dayIndex,
                        swimlaneIndex
                      )
                    }
                    onRemoveSession={(sessionIndex) =>
                      onRemoveSession(dayIndex, swimlaneIndex, sessionIndex)
                    }
                    onDuplicateSession={(session) =>
                      onDuplicateSession(session, dayIndex, swimlaneIndex)
                    }
                    onAddSession={() => onAddSession(dayIndex, swimlaneIndex)}
                  />
                )
              )
            )}
          </div>
        );
      })}
    </BlockStack>
  );
};

// ========================
// MULTIPLE TRACKS INTEGRATED RENDERER
// ========================
interface MultipleTracksIntegratedRendererProps {
  agenda: AgendaDay[];
  tracks: AgendaSwimlane[];
  selectedTrackIndex: number;
  isMultipleDays: boolean;
  formatDate: (date: string) => string;
  onTrackSelect: (index: number) => void;
  onManageTracks: () => void;
  onAddSession: (dayIndex: number, swimlaneIndex: number) => void;
  onEditSession: (
    session: AgendaSession,
    sessionIndex: number,
    dayIndex: number,
    swimlaneIndex: number
  ) => void;
  onRemoveSession: (
    dayIndex: number,
    swimlaneIndex: number,
    sessionIndex: number
  ) => void;
  onDuplicateSession: (
    session: AgendaSession,
    dayIndex: number,
    swimlaneIndex: number
  ) => void;
}

const MultipleTracksIntegratedRenderer: React.FC<
  MultipleTracksIntegratedRendererProps
> = ({
  agenda,
  tracks,
  selectedTrackIndex,
  isMultipleDays,
  formatDate,
  onTrackSelect,
  onManageTracks,
  onAddSession,
  onEditSession,
  onRemoveSession,
  onDuplicateSession,
}) => {
  const { t } = useTranslation();

  return (
    <BlockStack gap="400">
      {/* Tracks Management Header */}
      {tracks.length > 0 && (
        <InlineStack align="space-between">
          <Button variant="plain" onClick={onManageTracks}>
            {t("eventDetails.agenda.manageTracks")}
          </Button>
        </InlineStack>
      )}

      {/* Tracks Tabs */}
      {tracks.length === 0 ? (
        <EmptyStateRenderer
          type="noTracks"
          onAction={onManageTracks}
          actionLabel={t("eventDetails.agenda.tracks.addTrack")}
        />
      ) : (
        <Box
          padding="0"
          borderWidth="025"
          borderColor="border"
          borderRadius="200"
          background="bg-surface"
        >
          <BlockStack gap="0">
            <Tabs
              tabs={tracks.map((track: AgendaSwimlane, index: number) => ({
                id: `track-${index}`,
                content:
                  getLocalizedText(track.title) ||
                  `${t("eventDetails.agenda.session.track")} ${index + 1}`,
                index,
              }))}
              selected={selectedTrackIndex}
              onSelect={onTrackSelect}
            />

            {/* Session Content - No Divider */}
            <Box paddingBlockStart="0">
              <BlockStack gap="400">
                {agenda
                  .map((day, dayIndex) => ({ day, dayIndex }))
                  .sort(
                    (a, b) =>
                      new Date(a.day.date).getTime() -
                      new Date(b.day.date).getTime()
                  )
                  .map(({ day, dayIndex }) => {
                    return (
                      <div key={`tracks-day-${dayIndex}-${day.date}`}>
                        {isMultipleDays && (
                          <Box
                            padding="200"
                            background="bg-surface-secondary"
                            borderRadius="0"
                          >
                            <Text as="h4" variant="bodyMd" fontWeight="medium">
                              {formatDate(day.date)}
                            </Text>
                          </Box>
                        )}

                        {day.swimlines[selectedTrackIndex] ? (
                          <SessionsListRenderer
                            sessions={
                              day.swimlines[selectedTrackIndex]?.sessions || []
                            }
                            dayIndex={dayIndex}
                            swimlaneIndex={selectedTrackIndex}
                            onEditSession={(session, sessionIndex) =>
                              onEditSession(
                                session,
                                sessionIndex,
                                dayIndex,
                                selectedTrackIndex
                              )
                            }
                            onRemoveSession={(sessionIndex) =>
                              onRemoveSession(
                                dayIndex,
                                selectedTrackIndex,
                                sessionIndex
                              )
                            }
                            onDuplicateSession={(session) =>
                              onDuplicateSession(
                                session,
                                dayIndex,
                                selectedTrackIndex
                              )
                            }
                            onAddSession={() =>
                              onAddSession(dayIndex, selectedTrackIndex)
                            }
                          />
                        ) : (
                          <EmptyStateRenderer
                            type="noSessions"
                            onAction={() =>
                              onAddSession(dayIndex, selectedTrackIndex)
                            }
                            actionLabel={t(
                              "eventDetails.agenda.program.addButton"
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
              </BlockStack>
            </Box>
          </BlockStack>
        </Box>
      )}
    </BlockStack>
  );
};

// ========================
// MAIN AGENDA CARD COMPONENT
// ========================
export default function AgendaCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: AgendaCardProps) {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();

  // Check if there are any agenda-related errors
  const hasAgendaErrors = useMemo(() => {
    return (
      fieldErrors.agenda ||
      Object.keys(fieldErrors).some((key) => key.startsWith("agenda."))
    );
  }, [fieldErrors]);

  // Extract and normalize current agenda data (ensures sessions are always sorted)
  const agenda = useMemo(() => {
    const rawAgenda = eventData.agenda || [];
    if (rawAgenda.length === 0) return [];
    
    return normalizeAgendaData(rawAgenda);
  }, [eventData.agenda]);

  const preferenceKey = eventId ?? "new";
  const inferredMultipleDays = agenda.length > 1;
  const inferredMultipleTracks = agenda.some(
    (day) => day.swimlines && day.swimlines.length > 1
  );

  // State for user preferences - these represent explicit user intent overrides
  const [multipleDaysDraft, setMultipleDaysDraft] = useState<{
    key: string;
    value: boolean;
  } | null>(null);
  const [multipleTracksDraft, setMultipleTracksDraft] = useState<{
    key: string;
    value: boolean;
  } | null>(null);

  const userWantsMultipleDays =
    multipleDaysDraft?.key === preferenceKey
      ? multipleDaysDraft.value
      : inferredMultipleDays;
  const userWantsMultipleTracks =
    multipleTracksDraft?.key === preferenceKey
      ? multipleTracksDraft.value
      : inferredMultipleTracks;

  const setUserWantsMultipleDays = useCallback(
    (value: boolean) => {
      setMultipleDaysDraft({ key: preferenceKey, value });
    },
    [preferenceKey]
  );

  const setUserWantsMultipleTracks = useCallback(
    (value: boolean) => {
      setMultipleTracksDraft({ key: preferenceKey, value });
    },
    [preferenceKey]
  );

  // Sync dateStart/dateEnd with agenda data
  useEffect(() => {
    if (agenda.length === 0) {
      return;
    }

    const dates = agenda.map((day) => day.date).filter(Boolean);
    if (dates.length === 0) {
      return;
    }

    const sortedDates = dates.sort();
    const dateStart = sortedDates[0];
    const dateEnd = sortedDates[sortedDates.length - 1];

    if (eventData.dateStart !== dateStart || eventData.dateEnd !== dateEnd) {
      onDataChange({ dateStart, dateEnd });
    }
  }, [agenda, eventData.dateStart, eventData.dateEnd, onDataChange]);

  // Derived state for actual agenda structure
  const hasMultipleTracks = agenda.some(
    (day) => day.swimlines && day.swimlines.length > 1
  );

  // Update agenda helper - automatically syncs dateStart/dateEnd with agenda dates
  const updateAgenda = useCallback(
    (newAgenda: AgendaDay[]) => {
      if (newAgenda.length === 0) {
        onDataChange({ agenda: newAgenda });
        return;
      }

      // Calculate min and max dates from agenda
      const dates = newAgenda.map((day) => day.date).filter(Boolean);
      if (dates.length > 0) {
        const sortedDates = dates.sort();
        const dateStart = sortedDates[0];
        const dateEnd = sortedDates[sortedDates.length - 1];
        
        onDataChange({ 
          agenda: newAgenda,
          dateStart,
          dateEnd
        });
      } else {
        onDataChange({ agenda: newAgenda });
      }
    },
    [onDataChange]
  );

  // Derived state that reflects actual agenda state
  const hasAgenda = agenda.length > 0;

  // Modal states
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [isTracksModalOpen, setIsTracksModalOpen] = useState(false);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);

  // Session modal context
  const [editingSession, setEditingSession] = useState<
    AgendaSession | undefined
  >();
  const [sessionModalMode, setSessionModalMode] = useState<"add" | "edit">(
    "add"
  );
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [currentSwimlaneIndex, setCurrentSwimlaneIndex] = useState(0);
  const [currentSessionIndex, setCurrentSessionIndex] = useState(-1);
  const [targetDayIndex, setTargetDayIndex] = useState(0);
  const [targetSwimlaneIndex, setTargetSwimlaneIndex] = useState(0);

  // Day modal context
  const [editingDay, setEditingDay] = useState<AgendaDay | undefined>();
  const [dayModalMode, setDayModalMode] = useState<"add" | "edit">("add");
  const [editingDayIndex, setEditingDayIndex] = useState(-1);

  // Track selection for multi-track view
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);

  // Date formatting utility
  const formatDate = useCallback(
    (dateStr: string) => {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) {
        return dateStr;
      }

      const locale = i18n.resolvedLanguage || i18n.language || "en-US";
      const dateFormatter = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const weekdayFormatter = new Intl.DateTimeFormat(locale, {
        weekday: "long",
      });

      return `${dateFormatter.format(date)} - ${weekdayFormatter.format(date)}`;
    },
    [i18n.language, i18n.resolvedLanguage]
  );

  const dayOptions = useMemo(
    () =>
      agenda.map((day, index) => ({
        label: formatDate(day.date),
        value: String(index),
      })),
    [agenda, formatDate]
  );

  const trackOptions = useMemo(
    () =>
      (agenda[targetDayIndex]?.swimlines || []).map((swimlane, index) => ({
        label:
          getLocalizedText(swimlane.title) ||
          `${t("eventDetails.agenda.tracks.trackTitle")} ${index + 1}`,
        value: String(index),
      })),
    [agenda, targetDayIndex, t]
  );

  const handleTargetDayChange = useCallback(
    (value: string) => {
      console.debug("[AgendaCard::handleTargetDayChange] updating target day");

      const nextDayIndex = Number(value);
      if (Number.isNaN(nextDayIndex) || !agenda[nextDayIndex]) {
        return;
      }

      setTargetDayIndex(nextDayIndex);
      const hasCurrentTrackInTargetDay = Boolean(
        agenda[nextDayIndex].swimlines[targetSwimlaneIndex]
      );
      setTargetSwimlaneIndex(hasCurrentTrackInTargetDay ? targetSwimlaneIndex : 0);
    },
    [agenda, targetSwimlaneIndex]
  );

  const handleTargetSwimlaneChange = useCallback((value: string) => {
    console.debug("[AgendaCard::handleTargetSwimlaneChange] updating target track");

    const nextSwimlaneIndex = Number(value);
    if (Number.isNaN(nextSwimlaneIndex)) {
      return;
    }

    setTargetSwimlaneIndex(nextSwimlaneIndex);
  }, []);

  // Configuration handlers
  const handleMultipleDaysChange = useCallback(
    (checked: boolean) => {
      setUserWantsMultipleDays(checked);

      if (!checked && agenda.length > 1) {
        // Keep the earliest day (matches sorted day rendering in UI)
        const earliestDay =
          [...agenda].sort((a, b) => {
            const aTime = new Date(a.date).getTime();
            const bTime = new Date(b.date).getTime();
            const normalizedATime = Number.isNaN(aTime) ? Number.MAX_SAFE_INTEGER : aTime;
            const normalizedBTime = Number.isNaN(bTime) ? Number.MAX_SAFE_INTEGER : bTime;
            return normalizedATime - normalizedBTime;
          })[0] ?? agenda[0];
        updateAgenda([earliestDay]);
      } else {
        // Always trigger onDataChange to show save bar, even if no agenda changes
        // This ensures the save bar appears when user changes preferences
        onDataChange({});
      }
    },
    [agenda, updateAgenda, onDataChange, setUserWantsMultipleDays]
  );

  const handleMultipleTracksChange = useCallback(
    (checked: boolean) => {
      setUserWantsMultipleTracks(checked);

      if (!checked) {
        // When disabling multiple tracks, merge all sessions into a single track
        const updatedAgenda = agenda.map((day: AgendaDay) => {
          // Collect all sessions from all tracks
          const allSessions =
            day.swimlines?.flatMap((swimlane) => swimlane.sessions || []) || [];

          return {
            ...day,
            swimlines: [
              {
                title: { bg: "Основна програма", en: "Main Program" },
                sessions: sortSessionsByTime(allSessions),
              },
            ],
          };
        });
        updateAgenda(updatedAgenda);
        setSelectedTrackIndex(0);
      } else if (checked && agenda.length > 0) {
        // When enabling multiple tracks, preserve existing sessions in the first track
        const updatedAgenda = agenda.map((day: AgendaDay) => {
          const existingSessions = day.swimlines?.[0]?.sessions || [];

          return {
            ...day,
            swimlines: [
              {
                title: { bg: "Основна програма", en: "Main Program" },
                sessions: existingSessions,
              },
            ],
          };
        });
        updateAgenda(updatedAgenda);
        setSelectedTrackIndex(0);
      } else {
        // Always trigger onDataChange to show save bar, even if no agenda changes
        // This ensures the save bar appears when user changes preferences
        onDataChange({});
      }
    },
    [agenda, updateAgenda, onDataChange, setUserWantsMultipleTracks]
  );

  const handleEventDateChange = useCallback(
    (date: string, updateAgendaAlso = true) => {
      if (updateAgendaAlso && eventData.agenda && eventData.agenda.length === 1) {
        // Update agenda, which will auto-sync dateStart/dateEnd
        const updatedAgenda = JSON.parse(JSON.stringify(eventData.agenda));
        updatedAgenda[0].date = date;
        updateAgenda(updatedAgenda);
      } else {
        // Direct date update (e.g., when called from handleDaySave with updateAgendaAlso=false)
        onDataChange({ dateStart: date, dateEnd: date });
      }
    },
    [onDataChange, updateAgenda, eventData.agenda]
  );

  // Session handlers
  const handleAddSession = useCallback(
    (dayIndex: number, swimlaneIndex: number) => {
      // If we don't have real agenda data yet, create it now
      if (!hasAgenda) {
        const newDay: AgendaDay = {
          date: eventData.dateStart ?? "",
          swimlines: hasMultipleTracks
            ? []
            : [
                {
                  title: { bg: "Основна програма", en: "Main Program" },
                  sessions: [],
                },
              ],
        };
        updateAgenda([newDay]);
      }

      setCurrentDayIndex(dayIndex);
      setCurrentSwimlaneIndex(swimlaneIndex);
      setCurrentSessionIndex(-1);
      setTargetDayIndex(dayIndex);
      setTargetSwimlaneIndex(swimlaneIndex);
      setEditingSession(undefined);
      setSessionModalMode("add");
      setIsSessionModalOpen(true);
    },
    [hasAgenda, eventData.dateStart, updateAgenda, hasMultipleTracks]
  );

  const handleEditSession = useCallback(
    (
      session: AgendaSession,
      sessionIndex: number,
      dayIndex: number,
      swimlaneIndex: number
    ) => {
      setCurrentDayIndex(dayIndex);
      setCurrentSwimlaneIndex(swimlaneIndex);
      setCurrentSessionIndex(sessionIndex);
      setTargetDayIndex(dayIndex);
      setTargetSwimlaneIndex(swimlaneIndex);
      setEditingSession(session);
      setSessionModalMode("edit");
      setIsSessionModalOpen(true);
    },
    []
  );

  const handleSessionSave = useCallback(
    (
      session: AgendaSession,
      placement?: { dayIndex: number; swimlaneIndex: number }
    ) => {
      const updatedAgenda = JSON.parse(JSON.stringify(agenda)) as AgendaDay[];

      const sourceDayIndex = currentDayIndex;
      const sourceSwimlaneIndex = currentSwimlaneIndex;

      if (!updatedAgenda[sourceDayIndex]?.swimlines[sourceSwimlaneIndex]) {
        console.error("Source day or swimlane not found");
        return;
      }

      if (sessionModalMode === "add") {
        updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions.push(
          session
        );
        updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions =
          sortSessionsByTime(
            updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions
          );
      } else {
        const sourceSessions =
          updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions;

        if (
          currentSessionIndex < 0 ||
          !sourceSessions ||
          !sourceSessions[currentSessionIndex]
        ) {
          console.error("Source session not found");
          return;
        }

        const targetDay = placement?.dayIndex ?? sourceDayIndex;
        const targetSwimlane = placement?.swimlaneIndex ?? sourceSwimlaneIndex;

        if (!updatedAgenda[targetDay]?.swimlines[targetSwimlane]) {
          console.error("Target day or swimlane not found");
          return;
        }

        const isSameContainer =
          sourceDayIndex === targetDay && sourceSwimlaneIndex === targetSwimlane;

        if (isSameContainer) {
          sourceSessions[currentSessionIndex] = session;
          updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions =
            sortSessionsByTime(sourceSessions);
        } else {
          sourceSessions.splice(currentSessionIndex, 1);
          updatedAgenda[targetDay].swimlines[targetSwimlane].sessions.push(
            session
          );

          updatedAgenda[sourceDayIndex].swimlines[sourceSwimlaneIndex].sessions =
            sortSessionsByTime(sourceSessions);
          updatedAgenda[targetDay].swimlines[targetSwimlane].sessions =
            sortSessionsByTime(
              updatedAgenda[targetDay].swimlines[targetSwimlane].sessions
            );
        }
      }

      updateAgenda(updatedAgenda);
      setIsSessionModalOpen(false);
    },
    [
      agenda,
      currentDayIndex,
      currentSwimlaneIndex,
      currentSessionIndex,
      sessionModalMode,
      updateAgenda,
    ]
  );

  const handleRemoveSession = useCallback(
    (dayIndex: number, swimlaneIndex: number, sessionIndex: number) => {
      const updatedAgenda = [...agenda];
      updatedAgenda[dayIndex].swimlines[swimlaneIndex].sessions.splice(
        sessionIndex,
        1
      );
      updateAgenda(updatedAgenda);
    },
    [agenda, updateAgenda]
  );

  const handleDuplicateSession = useCallback(
    (session: AgendaSession, dayIndex: number, swimlaneIndex: number) => {
      const updatedAgenda = JSON.parse(JSON.stringify(agenda));
      const duplicatedSession = { ...session };
      updatedAgenda[dayIndex].swimlines[swimlaneIndex].sessions.push(
        duplicatedSession
      );
      
      // Sort sessions by time after duplicating
      updatedAgenda[dayIndex].swimlines[swimlaneIndex].sessions =
        sortSessionsByTime(
          updatedAgenda[dayIndex].swimlines[swimlaneIndex].sessions
        );
      
      updateAgenda(updatedAgenda);
    },
    [agenda, updateAgenda]
  );

  // Day handlers
  const handleAddDay = useCallback(() => {
    setEditingDay(undefined);
    setDayModalMode("add");
    setEditingDayIndex(-1);
    setIsDayModalOpen(true);
  }, []);

  const handleAddNextDay = useCallback(() => {
    if (agenda.length === 0) {
      handleAddDay();
      return;
    }

    // Calculate next day based on the latest date
    const latestDate = Math.max(
      ...agenda.map((day) => new Date(day.date).getTime())
    );
    const nextDate = new Date(latestDate);
    nextDate.setDate(nextDate.getDate() + 1);
    const nextDay = nextDate.toISOString().split("T")[0];

    // Create the next day directly
    const newDay: AgendaDay = {
      date: nextDay,
      swimlines: [
        {
          title: { bg: "Основна програма", en: "Main Program" },
          sessions: [],
        },
      ],
    };

    const updatedAgenda = [...agenda, newDay];
    updateAgenda(updatedAgenda);
  }, [agenda, handleAddDay, updateAgenda]);

  const handleEditDay = useCallback(
    (dayIndex: number) => {
      setEditingDay(agenda[dayIndex]);
      setDayModalMode("edit");
      setEditingDayIndex(dayIndex);
      setIsDayModalOpen(true);
    },
    [agenda]
  );

  const handleDaySave = useCallback(
    (day: AgendaDay) => {
      const updatedAgenda = JSON.parse(JSON.stringify(agenda));

      if (dayModalMode === "add") {
        updatedAgenda.push(day);
      } else {
        updatedAgenda[editingDayIndex] = day;
      }

      // updateAgenda will automatically sync dateStart/dateEnd with the agenda dates
      updateAgenda(updatedAgenda);
      setIsDayModalOpen(false);
    },
    [agenda, dayModalMode, editingDayIndex, updateAgenda]
  );

  const handleRemoveDay = useCallback(
    (dayIndex: number) => {
      const updatedAgenda = agenda.filter((_, index) => index !== dayIndex);
      updateAgenda(updatedAgenda);
    },
    [agenda, updateAgenda]
  );

  // Track handlers
  const handleManageTracks = useCallback(() => {
    setIsTracksModalOpen(true);
  }, []);

  const handleTracksSave = useCallback(
    (tracks: AgendaSwimlane[], originalTrackIndices: number[]) => {
      const baseAgenda =
        agenda.length > 0
          ? agenda
          : [
              {
                date: eventData.dateStart ?? "",
                swimlines: [],
              } as AgendaDay,
            ];

      const updatedAgenda = baseAgenda.map((day: AgendaDay) => ({
        ...day,
        swimlines: tracks.map((track: AgendaSwimlane, trackIndex: number) => {
          const sourceTrackIndex = originalTrackIndices[trackIndex] ?? -1;
          const sourceSessions =
            sourceTrackIndex >= 0
              ? day.swimlines[sourceTrackIndex]?.sessions || []
              : [];

          return {
            ...track,
            sessions: sortSessionsByTime(sourceSessions),
          };
        }),
      }));
      updateAgenda(updatedAgenda);
      setSelectedTrackIndex((previousTrackIndex) => {
        if (tracks.length === 0) {
          return 0;
        }

        return Math.min(previousTrackIndex, tracks.length - 1);
      });
      setIsTracksModalOpen(false);
    },
    [agenda, eventData.dateStart, updateAgenda]
  );

  // Get current tracks for tabs
  const currentTracks = useMemo(() => {
    return hasAgenda && userWantsMultipleTracks
      ? agenda[0]?.swimlines || []
      : [];
  }, [hasAgenda, userWantsMultipleTracks, agenda]);

  // Create a mock agenda structure for display when no real agenda exists
  const displayAgenda = useMemo(() => {
    if (hasAgenda) return agenda;

    // Return a mock agenda structure for display purposes only
    return [
      {
        date: eventData.dateStart,
        swimlines: userWantsMultipleTracks
          ? []
          : [
              {
                title: { bg: "Основна програма", en: "Main Program" },
                sessions: [],
              },
            ],
      },
    ] as AgendaDay[];
  }, [hasAgenda, agenda, eventData.dateStart, userWantsMultipleTracks]);

  // Determine if we should show the program interface
  const shouldShowProgram = hasAgenda || !userWantsMultipleDays;

  return (
    <>
      <Card background={hasAgendaErrors ? "bg-surface-critical" : undefined}>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd" requiredIndicator>
            {t("eventDetails.agenda.title")}
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            {t("eventDetails.agenda.description")}
          </Text>

          {/* Timezone Selection */}
          <TimezoneAutoComplete
            value={eventData.timezone ?? null}
            onChange={(timezone: Timezone | null) => {
              onDataChange({ timezone: timezone ?? undefined });
            }}
            label={t("eventDetails.agenda.timezone")}
            requiredIndicator={true}
            autopick={!eventData.timezone}
            error={fieldErrors.timezone}
          />

          {/* Configuration Section */}
          <ConfigurationRenderer
            isMultipleDays={userWantsMultipleDays}
            hasMultipleTracks={userWantsMultipleTracks}
            eventDate={eventData.dateStart ?? ""}
            onMultipleDaysChange={handleMultipleDaysChange}
            onMultipleTracksChange={handleMultipleTracksChange}
            onEventDateChange={handleEventDateChange}
            agenda={agenda}
            onAddDay={handleAddDay}
            onAddNextDay={handleAddNextDay}
            onEditDay={handleEditDay}
            onRemoveDay={handleRemoveDay}
          />

          {/* Multiple Tracks Integrated Section */}
          {userWantsMultipleTracks ? (
            <MultipleTracksIntegratedRenderer
              agenda={displayAgenda}
              tracks={currentTracks}
              selectedTrackIndex={selectedTrackIndex}
              isMultipleDays={userWantsMultipleDays}
              formatDate={formatDate}
              onTrackSelect={setSelectedTrackIndex}
              onManageTracks={handleManageTracks}
              onAddSession={handleAddSession}
              onEditSession={handleEditSession}
              onRemoveSession={handleRemoveSession}
              onDuplicateSession={handleDuplicateSession}
            />
          ) : (
            <>
              {!userWantsMultipleDays && (
                <Bleed marginInline="400">
                  <Divider />
                </Bleed>
              )}

              {/* Single Track Program/Sessions Section */}
              <BlockStack gap="300">
                {!userWantsMultipleDays && (
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h3" variant="headingSm">
                      {t("eventDetails.agenda.program.title")}
                    </Text>
                  </InlineStack>
                )}

                {!shouldShowProgram ? (
                  <></>
                ) : (
                  <ProgramContentRenderer
                    agenda={displayAgenda}
                    isMultipleDays={userWantsMultipleDays}
                    hasMultipleTracks={hasMultipleTracks}
                    selectedTrackIndex={selectedTrackIndex}
                    formatDate={formatDate}
                    onAddSession={handleAddSession}
                    onEditSession={handleEditSession}
                    onRemoveSession={handleRemoveSession}
                    onDuplicateSession={handleDuplicateSession}
                    onManageTracks={handleManageTracks}
                  />
                )}
              </BlockStack>
            </>
          )}

          {fieldErrors.agenda && (
            <Text as="p" variant="bodyMd" tone="critical">
              {fieldErrors.agenda}
            </Text>
          )}
        </BlockStack>
      </Card>

      {/* Modals */}
      <AddEditSessionModal
        key={`${isSessionModalOpen}-${sessionModalMode}-${currentSessionIndex}`}
        isOpen={isSessionModalOpen}
        onClose={() => setIsSessionModalOpen(false)}
        onSave={handleSessionSave}
        session={editingSession}
        isEditMode={sessionModalMode === "edit"}
        eventInstructorIds={eventData.instructorIds}
        targetDayIndex={targetDayIndex}
        targetSwimlaneIndex={targetSwimlaneIndex}
        dayOptions={dayOptions}
        trackOptions={trackOptions}
        showDaySelector={sessionModalMode === "edit" && agenda.length > 1}
        showTrackSelector={sessionModalMode === "edit" && trackOptions.length > 1}
        onTargetDayChange={handleTargetDayChange}
        onTargetSwimlaneChange={handleTargetSwimlaneChange}
      />

      <ManageTracksModal
        key={`${isTracksModalOpen}-${currentTracks.length}`}
        isOpen={isTracksModalOpen}
        onClose={() => setIsTracksModalOpen(false)}
        tracks={currentTracks}
        onSave={handleTracksSave}
      />

      <AddEditDayModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        onSave={handleDaySave}
        day={editingDay}
        isEditMode={dayModalMode === "edit"}
      />
    </>
  );
}
